/**
 * AdminAuditPage - Phase 2C-3
 *
 * Admin page for viewing audit log.
 * BSL Compliant: Factual data only, neutral colors.
 */

import { useAuditLog } from '../../hooks/useAuditLog';
import { AuditLogFilters as FilterType } from '../../types';
import { Card, CardHeader, CardTitle, CardContent, Button, LoadingSpinner, Alert } from '../../components/common';
import { AdminNav, AuditLogTable, AuditLogFilters } from '../../components/admin';

export function AdminAuditPage(): JSX.Element {
  const {
    entries,
    total,
    hasMore,
    loading,
    error,
    page,
    pageSize,
    setPage,
    filters,
    setFilters,
    refetch,
    actionTypes,
    loadingActionTypes,
  } = useAuditLog();

  const handleFilterChange = (newFilters: FilterType) => {
    setFilters(newFilters);
    setPage(1);
  };

  const handleResetFilters = () => {
    setFilters({});
    setPage(1);
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="flex gap-6 p-6">
      {/* Sidebar */}
      <aside className="w-56 flex-shrink-0">
        <div className="sticky top-6">
          <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">
            Admin
          </h2>
          <AdminNav />
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Audit Log</h1>
          <p className="text-slate-400 mt-1">Track all administrative actions</p>
        </div>

        {/* Messages */}
        {error && <Alert variant="error">{error}</Alert>}

        {/* Filters */}
        <AuditLogFilters
          filters={filters}
          onChange={handleFilterChange}
          actionTypes={actionTypes}
          loadingActionTypes={loadingActionTypes}
          onReset={handleResetFilters}
        />

        {/* Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>
                Audit Entries
                {total > 0 && (
                  <span className="ml-2 text-sm font-normal text-slate-400">
                    ({total} total)
                  </span>
                )}
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={refetch}>
                Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <LoadingSpinner size="lg" />
              </div>
            ) : (
              <AuditLogTable entries={entries} />
            )}

            {/* Pagination */}
            {total > 0 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-700">
                <p className="text-sm text-slate-400">
                  Showing {(page - 1) * pageSize + 1} - {Math.min(page * pageSize, total)} of {total}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setPage(page + 1)}
                    disabled={!hasMore && page >= totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Export Section */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-slate-300">Export Audit Log</h3>
                <p className="text-xs text-slate-500 mt-1">
                  Download audit log data for external analysis
                </p>
              </div>
              <Button variant="secondary" size="sm" disabled>
                Export CSV (Coming Soon)
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

export default AdminAuditPage;
